import {
  Skeleton,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TrailerTableRow } from './trailer-table-row'

import type { SortState } from '@/components/shared/sortable-table-head'
import type { VehicleWithTrailer } from '@repo/shared'

import { ListPagination } from '@/components/shared/list-pagination'
import {
  SortableTableHead,
  sortItems,
  toggleSort,
} from '@/components/shared/sortable-table-head'
import { useTrailersPaginated } from '@/features/vehicles/hooks/use-trailers-paginated'

type TrailerListProps = {
  search?: string
}

const trailerAccessor = (item: VehicleWithTrailer, key: string) => {
  switch (key) {
    case 'licensePlate':
      return item.licensePlate
    case 'manufacturer':
      return item.manufacturer
    case 'trailerType':
      return item.trailer?.type
    case 'status':
      return item.status
    case 'maxWeight':
      return item.trailer?.maxWeight
    default:
      return null
  }
}

export function TrailerList({ search }: TrailerListProps) {
  const { t } = useTranslation('vehicles')

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sort, setSort] = useState<SortState>(null)

  const { data: trailersData, isLoading } = useTrailersPaginated({
    page,
    pageSize,
    search: search || undefined,
  })

  useEffect(() => {
    setPage(1)
  }, [search])

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setPage(1)
  }

  const handleSort = (key: string) => setSort((prev) => toggleSort(prev, key))

  const sortedTrailers = useMemo(
    () => sortItems(trailersData?.items ?? [], sort, trailerAccessor),
    [trailersData?.items, sort],
  )

  return (
    <>
      {isLoading ? (
        <div className="overflow-hidden rounded-lg border">
          <Skeleton className="h-10 w-full rounded-none" />
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-none border-t" />
          ))}
        </div>
      ) : trailersData?.items && trailersData.items.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <SortableTableHead
                    sortKey="licensePlate"
                    sort={sort}
                    onSort={handleSort}
                  >
                    {t('table.vehicle')}
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="manufacturer"
                    sort={sort}
                    onSort={handleSort}
                  >
                    {t('table.manufacturer')}
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="trailerType"
                    sort={sort}
                    onSort={handleSort}
                  >
                    {t('table.trailerType')}
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="status"
                    sort={sort}
                    onSort={handleSort}
                  >
                    {t('table.status')}
                  </SortableTableHead>
                  <TableHead>{t('table.dimensions')}</TableHead>
                  <SortableTableHead
                    sortKey="maxWeight"
                    sort={sort}
                    onSort={handleSort}
                  >
                    {t('table.maxWeight')}
                  </SortableTableHead>
                  <TableHead>{t('table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTrailers.map((trailer) => (
                  <TrailerTableRow key={trailer.id} trailer={trailer} />
                ))}
              </TableBody>
            </Table>
          </div>

          <ListPagination
            page={trailersData.page}
            totalPages={trailersData.totalPages}
            pageSize={trailersData.pageSize}
            totalCount={trailersData.totalCount}
            hasPrevious={trailersData.hasPrevious}
            hasNext={trailersData.hasNext}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            className="mt-4 sm:px-4"
          />
        </>
      ) : (
        <div className="py-12 text-center">
          <p className="text-muted-foreground text-sm">
            {t('noVehiclesFound')}
          </p>
        </div>
      )}
    </>
  )
}
